'use client'

import { useCallback, useMemo } from 'react'
import { IconCreditCard, IconInfoSquare } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'

import { Cart } from 'modules/cart'
import {
  MarketingConsentsCheckboxes,
  MarketingConsentsValues,
  useCustomerData
} from 'modules/customer'
import { IS_TMS_DELIVERY } from 'shared/config'
import { Collapse } from 'shared/ui/Collapse'
import Checkbox from 'shared/ui/form/Checkbox'
import Input from 'shared/ui/form/Input'
import { ValidationResponseTypes } from 'shared/ui/form/inputs'
import { RadioGroup, RadioItem } from 'shared/ui/RadioGroup'
import { theme } from 'shared/ui/utils/designTokens'
import { PATHS } from 'shared/utils/paths'

import { PaymentCodes , PaymentMethod } from '../../payment/types'

import styles from './CheckoutPayment.module.scss'

// Input forwards `className` via `...rest` without declaring it, hence the spread
const blikInputClassName = { className: styles.blikInput }

const paymentMethodOrder: string[] = [
  PaymentCodes.BLIK,
  PaymentCodes.PAYNOW,
  PaymentCodes.DPAY,
  PaymentCodes.ON_DELIVERY,
  PaymentCodes.BLIK_ONE_CLICK
]

type ValidateFormFields = (
  name: string,
  callback: (v: ValidationResponseTypes) => void
) => Promise<void>
interface CheckoutPaymentProps {
  cart: Cart | null
  paymentMethods: PaymentMethod[]
  selectedMethod: string | null
  onChange: (method: PaymentMethod) => void
  rulesAccepted: boolean
  setRulesAccepted: (state: boolean) => void
  setAdditionalAgreementsValues: (values: MarketingConsentsValues) => void
  blikCode: string
  setBlikCode: (name: string, value: string) => void
  validateFormFields: ValidateFormFields
  additionalAgreementsValues: MarketingConsentsValues
}

const CheckoutPayment = ({
  cart,
  paymentMethods,
  selectedMethod,
  onChange,
  rulesAccepted,
  setRulesAccepted,
  blikCode,
  setBlikCode,
  validateFormFields,
  additionalAgreementsValues,
  setAdditionalAgreementsValues
}: CheckoutPaymentProps): React.ReactNode => {
  const { updatedUser: customer } = useCustomerData()

  const getPaymentMethodSortIndex = useCallback((methodCode: string) => {
    const index = paymentMethodOrder.indexOf(methodCode)

    return index === -1 ? paymentMethodOrder.length : index
  }, [])

  const handleOnChange = useCallback(
    (value: string) => {
      const methodToSet = paymentMethods.find(method => method.id === value)

      methodToSet && onChange(methodToSet)
    },
    [paymentMethods, onChange]
  )

  const visiblePaymentMethods = useMemo(
    () =>
      paymentMethods
        .filter(method => {
          if (method.code === PaymentCodes.BLIK_ONE_CLICK) {
            return false
          }

          if (cart?.editMode === true && selectedMethod !== method.id) {
            return false
          }

          return true
        })
        .sort(
          (a, b) =>
            getPaymentMethodSortIndex(a.code) -
            getPaymentMethodSortIndex(b.code)
        ),
    [paymentMethods, cart?.editMode, selectedMethod, getPaymentMethodSortIndex]
  )

  const renderRadioButtonContent = (paymentMethod: PaymentMethod) => {
    switch (paymentMethod.code) {
      case PaymentCodes.BLIK:
        return (
          <div className={styles.paymentTitle}>
            <span className={styles.paymentText}>Kod BLIK</span>
            <span className={styles.paymentImage}>
              <Image
                src='/static/blik.webp'
                width={60}
                height={30}
                alt='blik'
              />
            </span>
          </div>
        )
      case PaymentCodes.BLIK_ONE_CLICK:
        return (
          <div className={styles.paymentTitle}>
            <span className={styles.paymentText}>Płatność BLIK One-Click</span>
            <span className={styles.paymentImage}>
              <Image
                src='/static/blik.webp'
                width={60}
                height={30}
                alt='blik one-click'
              />
            </span>
          </div>
        )
      case PaymentCodes.DPAY:
        return (
          <div className={styles.paymentTitle}>
            <span className={styles.paymentText}>Przelew online</span>
            <span className={styles.paymentImage}>
              <Image
                src='/static/dpay-black.svg'
                width={88}
                height={30}
                alt='dpay'
              />
            </span>
          </div>
        )
      default:
        return (
          <div className={styles.paymentTitle}>
            <span className={styles.paymentText}>{paymentMethod.name}</span>
            <span className={styles.paymentImage}>
              <IconCreditCard />
            </span>
          </div>
        )
    }
  }

  const { hideNewsletterAgreement, hideSmsConsentsAgreement } = useMemo(() => {
    return {
      hideNewsletterAgreement: customer?.isNewsletterSubscriber,
      hideSmsConsentsAgreement: customer?.smsConsentsGranted
    }
  }, [customer])

  const hideAdditionalAgreements =
    hideNewsletterAgreement && hideSmsConsentsAgreement

  return (
    <div className={styles.wrapper}>
      <fieldset className={styles.paymentMethodSelectionWrapper}>
        <legend className={styles.title}>
          Metoda płatności
          <p className={styles.subtitle}>
            Wybierz jedną z poniższych opcji aby sfinalizować zamówienie
          </p>
        </legend>
        <RadioGroup
          name='paymentMethod'
          selectedValue={selectedMethod}
          onChange={value => handleOnChange(value)}
          variant='boxed'
        >
          {visiblePaymentMethods.map(method => {
            const isBlik = method.code === PaymentCodes.BLIK

            const isExpanded =
              isBlik && selectedMethod === method.id && !cart?.editMode

            return (
              <div className={styles.paymentWrapper} key={method.id}>
                <RadioItem value={method.id}>
                  {renderRadioButtonContent(method)}
                </RadioItem>
                {isBlik && (
                  <Collapse open={isExpanded} aria-hidden={!isExpanded}>
                    <Input
                      {...blikInputClassName}
                      name='blikCode'
                      size='medium'
                      value={blikCode}
                      onChange={setBlikCode}
                      validate={validateFormFields}
                      label='Wpisz 6-cio cyfrowy kod BLIK'
                      autoFocus
                      autoComplete='off'
                      aria-live='polite'
                      id='blikCode'
                    />
                  </Collapse>
                )}
                {IS_TMS_DELIVERY &&
                  method.code === PaymentCodes.ON_DELIVERY &&
                  selectedMethod === method.id && (
                    <div className={styles.informationBoxWrapper}>
                      <IconInfoSquare
                        width={24}
                        height={24}
                        color={theme.colors.brand.primary}
                      />
                      <p className={styles.informationBoxText}>
                        Akceptuję, że potwierdzenie płatności kartą otrzymam w
                        formie elektronicznej na swój adres e-mail podany w
                        trakcie rejestracji konta
                      </p>
                    </div>
                  )}
              </div>
            )
          })}
        </RadioGroup>
      </fieldset>

      {!cart?.editMode && (
        <div
          {...{ checked: rulesAccepted }}
          className={clsx(styles.statuteWrapper, {
            [styles.statuteWrapperChecked]: rulesAccepted
          })}
        >
          <div className={styles.statuteWrapperLabel}>Zaakceptuj regulamin</div>
          <Checkbox
            noMargin={hideAdditionalAgreements}
            value={rulesAccepted}
            onChange={(_, value) => setRulesAccepted(value)}
            name='regulationsAccepted'
            required={true}
            label={
              <div className={styles.statuteLabel}>
                Akceptuję
                <a
                  href={PATHS.termsAndConditions}
                  rel='noreferrer'
                  target='_blank'
                >
                  regulamin lokalnyrolnik.pl
                </a>
              </div>
            }
          />
          <div
            className={clsx(styles.animatedAgreementsWrapper, {
              [styles.animatedAgreementsWrapperVisible]:
                !hideAdditionalAgreements
            })}
          >
            {!hideAdditionalAgreements && (
              <MarketingConsentsCheckboxes
                noMargin
                values={additionalAgreementsValues}
                onChange={setAdditionalAgreementsValues}
                hideNewsletterAgreement={hideNewsletterAgreement}
                hideSmsConsentsAgreement={hideSmsConsentsAgreement}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CheckoutPayment
